import { useEffect, useRef, useState } from "react";
import { compareDesc, isBefore, isToday, parse, startOfDay, subDays } from "date-fns";
import { useGetSolicitations } from "../../hooks/useWallet";

import { db } from "~/db/db";
import { liveQuery } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";
import useWorker from "~hooks/useWorker";
import { getSessionStorage, removeSessionStorage, saveSessionStorage } from "~services/utils/SaveIndexBD";
import useVendedor from "~fetchs/useVendedor";

const useRequest = ({ skip, take }) => {
  const { vendedorSelecionado, vendedorId } = useVendedor();

  const { data: originalData } = useGetSolicitations(vendedorId);
  const [loading, setLoading] = useState(true);
  const autoSwitchedPeriodo = useRef(false);
  const [filters, setFilters] = useState({
    busca: "",
    status: "",
    periodo: "trintaDias",
    tipoSolicitacao: "",
    order: "",
  });

  useEffect(() => {
    const savedFilters = getSessionStorage("filtro-solicitacoes", true);
    if (savedFilters) {
      if (!((savedFilters.busca && !filters.busca) || (savedFilters.status && !filters.status) || (savedFilters.periodo && !filters.periodo) || (savedFilters.tipoSolicitacao && !filters.tipoSolicitacao) || (savedFilters.order && !filters.order))) {
        saveSessionStorage("filtro-solicitacoes", filters, true);
      }
    }
    else {
      saveSessionStorage("filtro-solicitacoes", filters, true);
    }
  }, [filters])

  useEffect(() => {
    const savedFilters = getSessionStorage("filtro-solicitacoes", true);
    if (savedFilters) setFilters(savedFilters);
  }, [])

  const { save } = useWorker({
    table: "solicitacoes",
  });

  useEffect(() => {
    if (originalData) {
      save(originalData);

      const solicitacoesSize = db.solicitacoes.count();

      if (originalData?.length && solicitacoesSize !== originalData.length) {
        const ids = originalData.map(item => item.id);
        db.solicitacoes.where('id').noneOf(ids).delete()
      }
    }
  }, [originalData]);

  const normalizeString = (str) => {
    // Verifica se str existe e é uma string
    if (!str || typeof str !== 'string') return '';
    return str.toLowerCase().normalize("NFD").replaceAll(/[\u0300-\u036f\s]/g, "");
  };

  const normalizeStringNumber = (str) => {
    // Verifica se str existe e é uma string
    if (!str || typeof str !== 'string') return '';
    // Remove caracteres especiais, exceto números
    return str.replace(/[^\d]/g, '').toLowerCase();
  };

  function getQuery() {
    const { status, busca, periodo, tipoSolicitacao } = filters;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startDate = new Date(today);
    startDate.setHours(0, 0, 0, 0);

    if (periodo === "trintaDias") {
      startDate.setDate(today.getDate() - 30);
    } else if (periodo === "quinzeDias") {
      startDate.setDate(today.getDate() - 15);
    } else if (periodo === "seteDias") {
      startDate.setDate(today.getDate() - 7);
    }
    let queryBuilder = db.solicitacoes.orderBy("id");
    if (filters.order !== "asc") {
      queryBuilder = queryBuilder.reverse();
    }

    // Normaliza a busca uma única vez para melhor performance
    const buscaNormalizada = busca ? normalizeString(busca) : '';
    const buscaNumerica = busca ? normalizeStringNumber(busca) : '';

    queryBuilder = queryBuilder.filter((item) =>
      (status ? item.statusSolicitacaoId === parseInt(status) : true) &&
      (tipoSolicitacao ? parseInt(item.tipoSolicitacao) === parseInt(tipoSolicitacao) : true) &&
      (periodo ? isBefore(startDate, new Date(item.createdAt)) : true) &&
      (vendedorSelecionado !== null ? item.usuarioId === vendedorSelecionado : true) &&
      (busca
        ? [
            normalizeString(item.codCliente),
            normalizeString(item.descricaoCliente ?? ''),
            normalizeStringNumber(item.descricaoCliente ?? ''),
            normalizeStringNumber(item.id?.toString() ?? ''),
            normalizeString(item.infoPlus ?? ''),
            normalizeStringNumber(item.infoPlus ?? ''),
          ].some(
            (str) =>
              str.includes(buscaNormalizada) ||
              str.includes(buscaNumerica)
          )
        : true)
    );

    return queryBuilder;
  }

  const [list, setList] = useState([]);

  useLiveQuery(() => {
    const fetchList = async () => {
      const result = [];
      const listQuery = getQuery();

      await listQuery
        .offset(0)
        .limit(skip + take)
        .each((item) => {
          result.push(item);
        });

      setList(result);
    };

    fetchList();
  }, [filters, take, skip, vendedorSelecionado]);

  const total = useLiveQuery(() => {
    let listQuery = getQuery();
    return listQuery.count();
  }, [filters, vendedorSelecionado])

  useEffect(() => {
    if (list !== undefined) {
      setLoading(false);
    }
  }, [list])

  useEffect(() => {
    autoSwitchedPeriodo.current = false;
  }, [vendedorSelecionado]);

  useEffect(() => {
    if (
      !autoSwitchedPeriodo.current &&
      filters.periodo === "trintaDias" &&
      originalData !== undefined &&
      total !== undefined &&
      total === 0
    ) {
      autoSwitchedPeriodo.current = true;
      setFilters(prev => ({ ...prev, periodo: "" }));
    }
  }, [total, filters.periodo, originalData]);

  const cleanFilters = () => {
    removeSessionStorage("filtro-solicitacoes");
    setFilters((prevState) => ({
      ...prevState,
      busca: "",
      status: "",
      periodo: "",
      tipoSolicitacao: "",
    }));
  };

  return {
    total,
    list,
    setFilters,
    cleanFilters,
    filters,
    loading,
    setLoading
  };
};

export default useRequest;